import { toJS } from 'mobx';
import { observer } from 'mobx-react';
import React from 'react';
import { FormFeedback, FormGroup, Input, Label } from 'reactstrap';
import BusyButton from '~/components/BusyButton';
import ErrorAlert from '~/components/ErrorAlert';
import ErrorList from '~/components/ErrorList';
import ViewInMpanel from '~/components/ViewInMpanel';
import WaitFor from '~/components/WaitFor';
import { DomainRequest, Network, Server } from '~/lib/ApiModel';
import { IPanelAppContext } from '~/lib/AppContext';
import { hasValue } from '~/lib/helper/ArrayHelper';
import { primaryInterface } from '~/lib/helper/NetworkHelper';
import { domainRoute } from '~/lib/helper/RouteHelper';
import { AsyncStore } from '~/lib/store/AsyncStore';
import { DomainsStore } from '~/lib/store/DomainsStore';
import FormStore from '~/lib/store/FormStore';
import { LoadingTracker } from '~/lib/store/LoadingTracker';
import { ServersStore } from '~/lib/store/ServersStore';

type DomainHostingAddPageProps = {
    appContext: IPanelAppContext;
};

@observer
export default class DomainHostingAddPage extends React.Component<DomainHostingAddPageProps> {
    domains: DomainsStore = new DomainsStore(this.props.appContext.apiClient);
    nameservers = new AsyncStore<string[]>(() => this.props.appContext.apiClient.getNameServers(), []);
    servers = new ServersStore(this.props.appContext.apiClient);

    form = new FormStore<DomainRequest, keyof DomainRequest>({
        name: '',
        ip_address: '',
    });

    tracker = new LoadingTracker();

    componentDidMount() {
        void this.tracker.track(() => Promise.all([this.servers.load(), this.nameservers.load()]));
        void this.domains.load();
    }

    async doSubmit() {
        const req = toJS(this.form.values);
        if (req.ip_address === '') req.ip_address = null;
        const domain = await this.domains.addDomainHosting(req);
        this.props.appContext.navigate(domainRoute(domain.name));
    }

    render() {
        const form = this.form;
        return (
            <div className="domainHostingAddPage">
                <h3>Add DNS Hosting</h3>
                <ViewInMpanel section="add-domain-hosting" />
                <WaitFor operation={this.tracker} loadingText="Loading...">
                    {() => (
                        <>
                            <p>
                                We are able to provide DNS hosting for as many domains as you like. To do so, use your
                                registrar to specify the following nameservers
                            </p>
                            <ul>
                                {this.nameservers.item.map((i) => (
                                    <li key={i}>{i}</li>
                                ))}
                            </ul>
                            <hr />
                            <div className="domainHostingAddPageForm">
                                After configuring your nameservers, enter your domain into the text field below before
                                clicking &quot;Add&quot; to proceed:
                                <form
                                    onSubmit={(e) => {
                                        e.preventDefault();
                                        void this.form.doSubmit(() => this.doSubmit());
                                    }}
                                >
                                    <FormGroup className="mt-3">
                                        <Label for="domainNameText">Domain</Label>
                                        <Input
                                            id="domainNameText"
                                            placeholder="e.g. example.com"
                                            {...form.inputProps('name')}
                                        />
                                        <FormFeedback>
                                            <ErrorList errors={form.errorsFor('name')} />
                                        </FormFeedback>
                                    </FormGroup>
                                    <FormGroup>
                                        <Label for="domainNameSetup">Initial Record Setup</Label>
                                        <Input
                                            id="domainNameSetup"
                                            type="select"
                                            style={{ maxWidth: '500px' }}
                                            {...form.inputProps('ip_address')}
                                        >
                                            <option value="">I will perform initial setup</option>
                                            {...this.servers.items
                                                .map((s) => {
                                                    const intf = primaryInterface(s);
                                                    return intf ? ([s, intf] as [Server, Network]) : null;
                                                })
                                                .filter(hasValue)
                                                .filter(([, intf]) => intf.type == 'public')
                                                .map(([s, intf]) => (
                                                    <option key={intf.ip_address} value={intf.ip_address}>
                                                        Website hosted on {s.name} ({intf.ip_address}):)
                                                    </option>
                                                ))}
                                        </Input>
                                        <FormFeedback>
                                            <ErrorList errors={form.errorsFor('ip_address')} />
                                        </FormFeedback>
                                    </FormGroup>
                                    <FormGroup>
                                        <ErrorAlert error={form.lastError} />
                                        <BusyButton busy={this.form.isSubmitting} type="submit">
                                            Add
                                        </BusyButton>
                                    </FormGroup>
                                </form>
                            </div>
                        </>
                    )}
                </WaitFor>
            </div>
        );
    }
}
